import { useEffect, useState } from "react";
import {
  fetchEventsForAdmin,
  saveEventsToApi,
  uploadCroppedEventImage,
  deleteEventImage,
  resolveEventImageUrl,
  canCropEventImage,
} from "../../utils/contentApi";
import ImageCropper from "../../components/ImageCropper/ImageCropper";
import { safeJsonParse } from "../../utils/safeJson";

function createEmptyEvent() {
  return {
    id: `event-${Date.now()}`,
    title: "",
    description: "",
    imageUrl: "",
    startDate: "",
    endDate: "",
    isActive: true,
    category: "event",
    imageFit: "cover",
    imagePosition: "center",
  };
}

const ALLOWED_IMAGE_POSITIONS = [
  "top",
  "center",
  "bottom",
  "left",
  "right",
  "top left",
  "top right",
  "bottom left",
  "bottom right",
];

function formatDateLabel(value) {
  const safe = String(value || "").trim();
  if (!safe) {
    return "-";
  }

  const parsed = new Date(`${safe}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) {
    return safe;
  }

  return parsed.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function EventAdmin() {
  const [events, setEvents] = useState([]);
  const [dailyMenuEvents, setDailyMenuEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [showCropper, setShowCropper] = useState(false);

  const [infoMessage, setInfoMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    let isMounted = true;

    const splitEvents = (sourceEvents) => {
      const safeEvents = Array.isArray(sourceEvents) ? sourceEvents : [];
      const popupMenus = safeEvents.filter(
        (eventItem) => eventItem?.category === "daily-menu",
      );
      const standardEvents = safeEvents.filter(
        (eventItem) => eventItem?.category !== "daily-menu",
      );

      return { popupMenus, standardEvents };
    };

    const loadEvents = async () => {
      setIsLoading(true);
      setErrorMessage("");

      const cachedEvents = localStorage.getItem("carpeDiemEvents");
      if (cachedEvents && isMounted) {
        const parsedEvents = safeJsonParse(cachedEvents, []);
        if (Array.isArray(parsedEvents)) {
          const { popupMenus, standardEvents } = splitEvents(parsedEvents);
          setDailyMenuEvents(popupMenus);
          setEvents(standardEvents);
        }
      }

      try {
        const remoteEvents = await fetchEventsForAdmin();
        if (!isMounted) {
          return;
        }

        const { popupMenus, standardEvents } = splitEvents(remoteEvents);
        setDailyMenuEvents(popupMenus);
        setEvents(standardEvents);
        localStorage.setItem("carpeDiemEvents", JSON.stringify(remoteEvents));
      } catch (_error) {
        if (!isMounted) {
          return;
        }

        setErrorMessage(
          "Impossible de charger les événements depuis le serveur.",
        );
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadEvents();

    return () => {
      isMounted = false;
    };
  }, []);

  const persistEvents = async (nextEvents, successMessage) => {
    setIsSaving(true);
    setErrorMessage("");
    setInfoMessage("");

    const safeNextEvents = Array.isArray(nextEvents) ? nextEvents : [];
    const payloadEvents = [...safeNextEvents, ...dailyMenuEvents];

    setEvents(nextEvents);
    localStorage.setItem("carpeDiemEvents", JSON.stringify(payloadEvents));

    try {
      const persisted = await saveEventsToApi(payloadEvents);
      const persistedDailyMenus = persisted.filter(
        (eventItem) => eventItem?.category === "daily-menu",
      );
      const persistedStandardEvents = persisted.filter(
        (eventItem) => eventItem?.category !== "daily-menu",
      );

      setDailyMenuEvents(persistedDailyMenus);
      setEvents(persistedStandardEvents);
      localStorage.setItem("carpeDiemEvents", JSON.stringify(persisted));
      window.dispatchEvent(new Event("carpe-content-changed"));
      setInfoMessage(successMessage);
      return { success: true };
    } catch (_error) {
      setErrorMessage(
        "La sauvegarde distante a échoué. Les données restent en cache local.",
      );
      return { success: false };
    } finally {
      setIsSaving(false);
    }
  };

  const openCreateForm = () => {
    setEditingEvent(createEmptyEvent());
    setShowForm(true);
    setErrorMessage("");
    setInfoMessage("");
  };

  const openEditForm = (eventItem) => {
    setEditingEvent({ ...eventItem });
    setShowForm(true);
    setErrorMessage("");
    setInfoMessage("");
  };

  const closeForm = () => {
    setEditingEvent(null);
    setShowForm(false);
  };

  const updateField = (field, value) => {
    setEditingEvent((prev) => ({ ...prev, [field]: value }));
  };

  const saveEvent = async () => {
    const title = String(editingEvent?.title || "").trim();
    const imageUrl = String(editingEvent?.imageUrl || "").trim();
    const endDate = String(editingEvent?.endDate || "").trim();

    if (!title) {
      setErrorMessage("Le titre de l'événement est obligatoire.");
      return;
    }

    if (!imageUrl) {
      setErrorMessage("L'URL de l'image est obligatoire.");
      return;
    }

    if (!endDate) {
      setErrorMessage("La date limite est obligatoire.");
      return;
    }

    const startDate = String(editingEvent?.startDate || "").trim();
    if (startDate && startDate > endDate) {
      setErrorMessage(
        "La date de début ne peut pas être après la date limite.",
      );
      return;
    }

    const normalizedEvent = {
      ...editingEvent,
      title,
      imageUrl,
      endDate,
      startDate,
      description: String(editingEvent?.description || "").trim(),
      isActive: editingEvent?.isActive !== false,
      category:
        editingEvent?.category === "daily-menu" ? "daily-menu" : "event",
      imageFit: editingEvent?.imageFit === "contain" ? "contain" : "cover",
      imagePosition: ALLOWED_IMAGE_POSITIONS.includes(
        editingEvent?.imagePosition,
      )
        ? editingEvent.imagePosition
        : "center",
    };

    const existingIndex = events.findIndex(
      (eventItem) => eventItem.id === normalizedEvent.id,
    );

    const nextEvents = [...events];
    if (existingIndex === -1) {
      nextEvents.push(normalizedEvent);
    } else {
      nextEvents[existingIndex] = normalizedEvent;
    }

    const result = await persistEvents(nextEvents, "Événement enregistré.");
    if (result.success) {
      closeForm();
    }
  };

  const deleteEvent = async (eventId) => {
    if (!window.confirm("Supprimer cet événement ?")) {
      return;
    }

    const deletedEvent = events.find((eventItem) => eventItem.id === eventId);
    const nextEvents = events.filter((eventItem) => eventItem.id !== eventId);
    await persistEvents(nextEvents, "Événement supprimé.");

    // Clean up uploaded images
    if (deletedEvent) {
      if (deletedEvent.imageUrl) {
        deleteEventImage(deletedEvent.imageUrl);
      }
      if (
        deletedEvent.originalImageUrl &&
        deletedEvent.originalImageUrl !== deletedEvent.imageUrl
      ) {
        deleteEventImage(deletedEvent.originalImageUrl);
      }
    }
  };

  const toggleActive = async (eventId) => {
    const nextEvents = events.map((eventItem) =>
      eventItem.id === eventId
        ? { ...eventItem, isActive: eventItem.isActive === false }
        : eventItem,
    );

    await persistEvents(nextEvents, "Statut de publication mis à jour.");
  };

  const moveEvent = async (eventId, direction) => {
    const currentIndex = events.findIndex(
      (eventItem) => eventItem.id === eventId,
    );
    const targetIndex = currentIndex + direction;

    if (
      currentIndex === -1 ||
      targetIndex < 0 ||
      targetIndex >= events.length
    ) {
      return;
    }

    const nextEvents = [...events];
    [nextEvents[currentIndex], nextEvents[targetIndex]] = [
      nextEvents[targetIndex],
      nextEvents[currentIndex],
    ];

    await persistEvents(nextEvents, "Ordre des événements mis à jour.");
  };

  const handleCrop = async (blob) => {
    try {
      const oldUrl = editingEvent.imageUrl;
      const newUrl = await uploadCroppedEventImage(blob);

      // Preserve the very first imageUrl as originalImageUrl
      const originalImageUrl =
        editingEvent.originalImageUrl || editingEvent.imageUrl;

      const updatedEvent = {
        ...editingEvent,
        imageUrl: newUrl,
        originalImageUrl,
      };
      setEditingEvent(updatedEvent);
      setShowCropper(false);

      // Auto-save: persist the updated event immediately
      const existingIndex = events.findIndex(
        (eventItem) => eventItem.id === updatedEvent.id,
      );
      if (existingIndex !== -1) {
        const nextEvents = [...events];
        nextEvents[existingIndex] = updatedEvent;
        await persistEvents(nextEvents, "Image recadrée et sauvegardée.");
      } else {
        setInfoMessage(
          "Image recadrée. N'oubliez pas d'enregistrer l'événement.",
        );
      }

      // Clean up the previous cropped image (not the original)
      if (oldUrl !== originalImageUrl) {
        deleteEventImage(oldUrl);
      }
    } catch (_error) {
      setErrorMessage("Échec de l'upload de l'image recadrée.");
    }
  };

  const handleResetImage = async () => {
    if (!editingEvent?.id) return;

    const originalUrl = editingEvent.originalImageUrl;
    if (!originalUrl) {
      setInfoMessage("Pas d'image d'origine enregistrée.");
      return;
    }

    const oldUrl = editingEvent.imageUrl;
    const updatedEvent = {
      ...editingEvent,
      imageUrl: originalUrl,
      originalImageUrl: "",
    };
    setEditingEvent(updatedEvent);

    const nextEvents = events.map((ev) =>
      ev.id === updatedEvent.id ? updatedEvent : ev,
    );
    await persistEvents(nextEvents, "Image d'origine restaurée.");
    setShowCropper(false);

    // Delete the old cropped image
    if (oldUrl !== originalUrl) {
      deleteEventImage(oldUrl);
    }
  };

  return (
    <div className="dashboard events-admin-page">
      {showCropper && editingEvent?.imageUrl ? (
        <ImageCropper
          imageUrl={resolveEventImageUrl(editingEvent.imageUrl)}
          onCrop={handleCrop}
          onCancel={() => setShowCropper(false)}
          onReset={editingEvent.originalImageUrl ? handleResetImage : undefined}
        />
      ) : null}
      <div className="dashboard-header">
        <h1>Gestion des événements</h1>
        <p className="dashboard-subtitle">
          Ces événements alimentent la popup d'accueil et la page Événements.
        </p>
        <p className="dashboard-subtitle">
          Les menus du jour se gèrent uniquement dans l'onglet Menus.
        </p>
      </div>

      <div className="events-admin-toolbar">
        <button
          type="button"
          className="refresh-btn reservation-open-create-btn"
          onClick={openCreateForm}
          disabled={isSaving}
        >
          + Ajouter un événement
        </button>
      </div>

      {infoMessage ? (
        <p className="reservations-state success">{infoMessage}</p>
      ) : null}
      {errorMessage ? (
        <p className="reservations-state error">{errorMessage}</p>
      ) : null}

      {showForm && editingEvent ? (
        <section className="reservation-settings-panel events-admin-form-panel">
          <div className="reservation-settings-header">
            <h2>
              {events.some((item) => item.id === editingEvent.id)
                ? "Modifier"
                : "Créer"}{" "}
              un événement
            </h2>
          </div>

          <div className="events-admin-form-grid">
            <div className="form-group">
              <label htmlFor="event-title">Titre *</label>
              <input
                id="event-title"
                className="reservation-filter-input"
                type="text"
                value={editingEvent.title}
                onChange={(event) => updateField("title", event.target.value)}
                maxLength={120}
              />
            </div>

            <div className="form-group">
              <label htmlFor="event-image">URL image *</label>
              <input
                id="event-image"
                className="reservation-filter-input"
                type="url"
                value={editingEvent.imageUrl}
                onChange={(event) =>
                  updateField("imageUrl", event.target.value)
                }
                placeholder="https://..."
              />
              {editingEvent.imageUrl ? (
                <>
                  <button
                    type="button"
                    className="event-admin-crop-btn"
                    onClick={() => {
                      if (canCropEventImage(editingEvent.imageUrl)) {
                        setShowCropper(true);
                      }
                    }}
                    disabled={isSaving || !canCropEventImage(editingEvent.imageUrl)}
                  >
                    Recadrer / Redimensionner
                  </button>
                  <img
                    className="event-admin-crop-preview"
                    src={resolveEventImageUrl(editingEvent.imageUrl)}
                    alt="Aperçu"
                    loading="lazy"
                    style={{
                      objectFit: editingEvent.imageFit || "cover",
                      objectPosition: editingEvent.imagePosition || "center",
                    }}
                  />
                </>
              ) : null}
              {editingEvent.imageUrl &&
              !canCropEventImage(editingEvent.imageUrl) ? (
                <p className="events-admin-image-help">
                  Le recadrage est disponible uniquement pour les images
                  uploadées sur ce site (/uploads/events). Cette URL externe
                  peut s'afficher mais ne peut pas être recadrée ici.
                </p>
              ) : null}
            </div>

            <div className="form-group">
              <label htmlFor="event-start-date">Date de début</label>
              <input
                id="event-start-date"
                className="reservation-filter-input"
                type="date"
                value={editingEvent.startDate || ""}
                onChange={(event) =>
                  updateField("startDate", event.target.value)
                }
              />
            </div>

            <div className="form-group">
              <label htmlFor="event-end-date">Date limite *</label>
              <input
                id="event-end-date"
                className="reservation-filter-input"
                type="date"
                value={editingEvent.endDate}
                onChange={(event) => updateField("endDate", event.target.value)}
              />
            </div>

            <div className="form-group">
              <label htmlFor="event-image-fit">Ajustement image (popup)</label>
              <select
                id="event-image-fit"
                className="reservation-filter-input"
                value={editingEvent.imageFit || "cover"}
                onChange={(event) =>
                  updateField("imageFit", event.target.value)
                }
              >
                <option value="cover">Remplir (cover)</option>
                <option value="contain">Afficher en entier (contain)</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="event-image-position">Position image (popup)</label>
              <select
                id="event-image-position"
                className="reservation-filter-input"
                value={editingEvent.imagePosition || "center"}
                onChange={(event) =>
                  updateField("imagePosition", event.target.value)
                }
              >
                <option value="center">Centre</option>
                <option value="top">Haut</option>
                <option value="bottom">Bas</option>
                <option value="left">Gauche</option>
                <option value="right">Droite</option>
                <option value="top left">Haut gauche</option>
                <option value="top right">Haut droite</option>
                <option value="bottom left">Bas gauche</option>
                <option value="bottom right">Bas droite</option>
              </select>
            </div>

            <div className="form-group events-admin-form-switch">
              <label className="reservation-switch">
                <input
                  type="checkbox"
                  checked={editingEvent.isActive !== false}
                  onChange={(event) =>
                    updateField("isActive", event.target.checked)
                  }
                />
                <span>Publier cet événement</span>
              </label>
            </div>

            <div className="form-group events-admin-form-full">
              <label htmlFor="event-description">Description</label>
              <textarea
                id="event-description"
                className="reservation-filter-input reservation-admin-message"
                value={editingEvent.description}
                onChange={(event) =>
                  updateField("description", event.target.value)
                }
                rows="5"
                maxLength={1000}
              />
            </div>
          </div>

          <div className="reservation-settings-footer">
            <button
              type="button"
              className="refresh-btn"
              onClick={closeForm}
              disabled={isSaving}
            >
              Annuler
            </button>
            <button
              type="button"
              className="refresh-btn reservation-open-create-btn"
              onClick={saveEvent}
              disabled={isSaving}
            >
              {isSaving ? "Enregistrement..." : "Enregistrer"}
            </button>
          </div>
        </section>
      ) : null}

      {isLoading ? (
        <p className="reservations-state">Chargement des événements...</p>
      ) : events.length === 0 ? (
        <p className="reservations-state">Aucun événement pour le moment.</p>
      ) : (
        <div className="events-admin-list">
          {events.map((eventItem, index) => (
            <article key={eventItem.id} className="event-admin-card">
              <div className="event-admin-media">
                <img
                  src={resolveEventImageUrl(eventItem.imageUrl)}
                  alt={eventItem.title}
                  loading="lazy"
                />
              </div>

              <div className="event-admin-content">
                <div className="event-admin-header">
                  <h3>{eventItem.title}</h3>
                  <div className="event-admin-badges">
                    <span className="event-admin-type">
                      {eventItem.category === "daily-menu"
                        ? "Menu du jour"
                        : "Événement"}
                    </span>
                    <span
                      className={`event-admin-badge${
                        eventItem.isActive === false ? " off" : ""
                      }`}
                    >
                      {eventItem.isActive === false ? "Masqué" : "Publié"}
                    </span>
                  </div>
                </div>

                <p className="event-admin-dates">
                  Début: {formatDateLabel(eventItem.startDate)} | Limite:{" "}
                  {formatDateLabel(eventItem.endDate)}
                </p>

                <p className="event-admin-description">
                  {eventItem.description || "Aucune description."}
                </p>

                <div className="event-admin-actions">
                  <button
                    type="button"
                    className="refresh-btn"
                    onClick={() => moveEvent(eventItem.id, -1)}
                    disabled={isSaving || index === 0}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className="refresh-btn"
                    onClick={() => moveEvent(eventItem.id, 1)}
                    disabled={isSaving || index === events.length - 1}
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    className="refresh-btn"
                    onClick={() => toggleActive(eventItem.id)}
                    disabled={isSaving}
                  >
                    {eventItem.isActive === false ? "Publier" : "Masquer"}
                  </button>
                  <button
                    type="button"
                    className="refresh-btn"
                    onClick={() => openEditForm(eventItem)}
                    disabled={isSaving}
                  >
                    Modifier
                  </button>
                  <button
                    type="button"
                    className="reservation-delete-btn"
                    onClick={() => deleteEvent(eventItem.id)}
                    disabled={isSaving}
                  >
                    Supprimer
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

export default EventAdmin;
